import React from 'react';
import { Shield, Mail, Phone, MapPin, ExternalLink, ArrowUp } from 'lucide-react';
import { mockData } from '../mock';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: [
      { label: 'Pentest de Infraestrutura', href: '#services' },
      { label: 'Pentest Web & Mobile', href: '#services' },
      { label: 'Teste de Redes Wi-Fi', href: '#services' },
      { label: 'Engenharia Social', href: '#services' }
    ],
    company: [
      { label: 'Sobre Nós', href: '#about' },
      { label: 'Nossa Equipe', href: '#team' },
      { label: 'Metodologia', href: '#methodology' },
      { label: 'Depoimentos', href: '#testimonials' }
    ],
    frameworks: [
      { label: 'OWASP', href: '#' },
      { label: 'MITRE ATT&CK', href: '#' },
      { label: 'NIST', href: '#' },
      { label: 'ISO 27001', href: '#' }
    ]
  };

  const socialLinks = [
    { 
      label: 'LinkedIn', 
      href: mockData.contact.linkedin,
      icon: '💼'
    },
    { 
      label: 'WhatsApp', 
      href: `https://wa.me/${mockData.contact.whatsapp}`,
      icon: '📱'
    },
    { 
      label: 'E-mail', 
      href: `mailto:${mockData.contact.email}`,
      icon: '📧'
    }
  ];

  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.open(href, '_blank');
    }
  };

  return (
    <footer className="bg-black border-t border-gray-800 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="cyber-grid opacity-5" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-r from-cyan-500/5 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <div className="lg:col-span-1">
              {/* Logo */}
              <div className="flex items-center space-x-3 mb-6">
                <div className="relative">
                  <Shield className="h-10 w-10 text-cyan-400 animate-pulse" />
                  <div className="absolute inset-0 h-10 w-10 text-cyan-400 animate-ping opacity-20">
                    <Shield className="h-10 w-10" />
                  </div>
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  NefilinSEC
                </span>
              </div>

              {/* Description */}
              <p className="body-medium text-gray-300 mb-6 leading-relaxed">
                Especialistas em segurança cibernética, oferecendo serviços 
                profissionais de pentest e análise de vulnerabilidades.
              </p>

              {/* Contact Info */}
              <div className="space-y-3">
                <div className="flex items-center space-x-3 text-gray-300">
                  <Phone className="w-4 h-4 text-cyan-400" />
                  <span className="body-small">{mockData.contact.phone}</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <Mail className="w-4 h-4 text-cyan-400" />
                  <span className="body-small">{mockData.contact.email}</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <MapPin className="w-4 h-4 text-cyan-400" />
                  <span className="body-small">{mockData.contact.address}</span>
                </div>
              </div>
            </div>

            {/* Services */}
            <div>
              <h3 className="heading-3 text-white mb-6">Serviços</h3>
              <ul className="space-y-3">
                {footerLinks.services.map((link, index) => (
                  <li key={index}>
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="body-small text-gray-400 hover:text-cyan-400 transition-colors duration-300 flex items-center space-x-2 group"
                    >
                      <span>{link.label}</span>
                      <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="heading-3 text-white mb-6">Empresa</h3>
              <ul className="space-y-3">
                {footerLinks.company.map((link, index) => (
                  <li key={index}>
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="body-small text-gray-400 hover:text-cyan-400 transition-colors duration-300 flex items-center space-x-2 group"
                    >
                      <span>{link.label}</span>
                      <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </button>
                  </li>
                ))}
              </ul>

              {/* Social Links */}
              <div className="mt-8">
                <h4 className="body-medium text-white mb-4 font-semibold">Redes Sociais</h4>
                <div className="flex space-x-4">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 glass-effect border border-cyan-400/30 rounded-full flex items-center justify-center hover:border-cyan-400 hover:scale-110 transition-all duration-300"
                      title={social.label}
                    >
                      <span className="text-lg">{social.icon}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Frameworks & Certifications */}
            <div>
              <h3 className="heading-3 text-white mb-6">Conformidade</h3>
              <ul className="space-y-3 mb-8">
                {footerLinks.frameworks.map((link, index) => (
                  <li key={index}>
                    <div className="body-small text-gray-400 flex items-center space-x-2">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full" />
                      <span>{link.label}</span>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Newsletter Signup */}
              <div className="glass-effect border border-cyan-400/30 rounded-lg p-4">
                <h4 className="body-medium text-white mb-2 font-semibold">Newsletter</h4>
                <p className="body-small text-gray-400 mb-4">
                  Receba atualizações sobre segurança cibernética
                </p>
                <div className="flex">
                  <input
                    type="email"
                    placeholder="Seu e-mail"
                    className="flex-1 px-3 py-2 bg-black/50 border border-gray-600 rounded-l-lg text-white text-sm placeholder-gray-400 focus:border-cyan-400 focus:outline-none"
                  />
                  <button className="px-4 py-2 bg-cyan-400 text-black rounded-r-lg hover:bg-cyan-300 transition-colors duration-300">
                    <Mail className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 py-8">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
            {/* Copyright */}
            <div className="text-center lg:text-left">
              <p className="body-small text-gray-400">
                © {currentYear} NefilinSEC. Todos os direitos reservados.
              </p>
              <p className="body-small text-gray-500 mt-1">
                CNPJ: {mockData.contact.cnpj}
              </p>
            </div>

            {/* Legal Links */}
            <div className="flex space-x-6">
              <button className="body-small text-gray-400 hover:text-cyan-400 transition-colors duration-300">
                Política de Privacidade
              </button>
              <button className="body-small text-gray-400 hover:text-cyan-400 transition-colors duration-300">
                Termos de Uso
              </button>
              <button className="body-small text-gray-400 hover:text-cyan-400 transition-colors duration-300">
                Cookies
              </button>
            </div>

            {/* Back to Top */}
            <button
              onClick={scrollToTop}
              className="w-10 h-10 glass-effect border border-cyan-400/30 rounded-full flex items-center justify-center hover:border-cyan-400 hover:scale-110 transition-all duration-300 group"
              title="Voltar ao topo"
            >
              <ArrowUp className="w-5 h-5 text-cyan-400 group-hover:scale-110 transition-transform duration-300" />
            </button>
          </div>
        </div>

        {/* Security Badge */}
        <div className="absolute bottom-4 right-4 glass-effect border border-cyan-400/30 rounded-lg p-3 animate-pulse">
          <div className="flex items-center space-x-2">
            <Shield className="w-4 h-4 text-cyan-400" />
            <span className="text-xs text-cyan-400 font-medium">Site Seguro</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;